// src/components/Vendors/VendorIntelligencePanel.jsx

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/axios";
import {
  AlertTriangle,
  ArrowLeft,
  BadgePoundSterling,
  CalendarClock,
  CheckCircle,
  FileText,
  RefreshCw,
  ShieldCheck,
  TrendingDown,
  Truck,
} from "lucide-react";

const currency = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const compactCurrency = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value || 0));

const percent = (value) =>
  `${(Number(value || 0) * 100).toFixed(1)}%`;

const number = (value) =>
  Number(value || 0).toLocaleString("en-GB");

function scoreClass(score) {
  const n = Number(score || 0);

  if (n >= 85) return "text-emerald-300";
  if (n >= 70) return "text-amber-300";

  return "text-red-300";
}

function scoreBarClass(score) {
  const n = Number(score || 0);

  if (n >= 85) return "bg-emerald-400";
  if (n >= 70) return "bg-amber-400";

  return "bg-red-400";
}

function riskClass(risk) {
  const safe = String(risk || "").toLowerCase();

  if (safe === "low") {
    return "border-emerald-400/30 bg-emerald-500/10 text-emerald-300";
  }

  if (safe === "medium") {
    return "border-amber-400/30 bg-amber-500/10 text-amber-300";
  }

  if (safe === "high") {
    return "border-red-400/30 bg-red-500/10 text-red-300";
  }

  return "border-white/10 bg-white/5 text-gray-300";
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-white/10 bg-[#0f1115] shadow-xl shadow-black/20 ${className}`}
    >
      {children}
    </div>
  );
}

function Metric({
  label,
  value,
  icon: Icon,
  tone = "text-white",
  hint,
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
          {Icon && <Icon className="h-4 w-4" />}
          {label}
        </div>
      </div>

      <div className={`mt-2 text-2xl font-bold ${tone}`}>
        {value}
      </div>

      {hint && (
        <div className="mt-1 text-xs text-gray-500">
          {hint}
        </div>
      )}
    </Card>
  );
}

function ScoreBar({ label, value }) {
  const n = Math.max(
    0,
    Math.min(100, Number(value || 0))
  );

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-gray-300">{label}</span>

        <span className={`font-bold ${scoreClass(n)}`}>
          {n}/100
        </span>
      </div>

      <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
        <div
          className={`h-full rounded-full transition-all duration-500 ${scoreBarClass(
            n
          )}`}
          style={{
            width: `${n}%`,
          }}
        />
      </div>
    </div>
  );
}

export default function VendorIntelligencePanel({
  vendorId,
  onBack,
}) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const fetchVendor = async ({ silent = false } = {}) => {
    if (!vendorId) {
      setError("No vendor was selected.");
      setLoading(false);
      return;
    }

    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    try {
      const response = await api.get(
        `/analytics/vendors/${vendorId}/intelligence`,
        {
          params: {
            _ts: Date.now(),
          },
        }
      );

      setData(response.data || null);
    } catch (err) {
      console.error(
        "Failed to load vendor intelligence:",
        err
      );

      setData(null);

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load vendor intelligence."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const vendor = data?.vendor || {};
  const summary = data?.summary || {};
  const scores = data?.scores || {};
  const products = Array.isArray(data?.products)
    ? data.products
    : [];
  const intelligence = data?.intelligence || {};

  const decisionPoints = Array.isArray(
    intelligence?.decision_points
  )
    ? intelligence.decision_points
    : [];

  const priceOpportunities = useMemo(
    () =>
      products.filter(
        (product) =>
          Number(
            product.saving_per_unit_vs_lowest || 0
          ) > 0
      ),
    [products]
  );

  const topSavingProducts = useMemo(
    () =>
      [...priceOpportunities]
        .sort(
          (a, b) =>
            Number(
              b.saving_per_unit_vs_lowest || 0
            ) -
            Number(
              a.saving_per_unit_vs_lowest || 0
            )
        )
        .slice(0, 5),
    [priceOpportunities]
  );

  const decision = useMemo(() => {
    const overall = Number(scores.overall || 0);
    const risk = String(
      data?.risk_level || ""
    ).toLowerCase();
    const opportunities =
      priceOpportunities.length;

    if (risk === "high" || overall < 70) {
      return {
        label: "Review Supplier",
        tone: "text-red-300",
        border:
          "border-red-400/30 bg-red-500/10",
        icon: AlertTriangle,
        message:
          "This supplier needs management review. Performance, risk, pricing or invoice exposure is below the expected operating standard.",
      };
    }

    if (opportunities >= 5 || overall < 85) {
      return {
        label: "Negotiate / Monitor",
        tone: "text-amber-300",
        border:
          "border-amber-400/30 bg-amber-500/10",
        icon: TrendingDown,
        message:
          "This supplier is operationally usable, but price opportunities or performance indicators suggest commercial review is required.",
      };
    }

    return {
      label: "Keep Supplier",
      tone: "text-emerald-300",
      border:
        "border-emerald-400/30 bg-emerald-500/10",
      icon: CheckCircle,
      message:
        "This supplier is performing well across procurement, delivery, invoice and pricing signals.",
    };
  }, [
    scores.overall,
    data?.risk_level,
    priceOpportunities.length,
  ]);

  const DecisionIcon = decision.icon;

  if (loading) {
    return (
      <Card className="p-6 text-white">
        <div className="flex items-center gap-3 text-gray-300">
          <RefreshCw className="h-5 w-5 animate-spin" />
          Loading vendor intelligence...
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="overflow-hidden text-white">
        <div className="border-b border-red-400/20 bg-red-500/10 p-6">
          <div className="flex flex-wrap items-start gap-4">
            <div className="rounded-xl border border-red-400/20 bg-red-500/10 p-3">
              <AlertTriangle className="h-6 w-6 text-red-300" />
            </div>

            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-red-200">
                Vendor intelligence could not be loaded
              </h2>

              <p className="mt-2 text-sm text-red-200/70">
                {error}
              </p>

              <p className="mt-2 text-xs text-gray-500">
                Vendor ID: {vendorId || "Not supplied"}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-200 hover:bg-white/10"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              )}

              <button
                type="button"
                onClick={() => fetchVendor()}
                className="inline-flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-200 hover:bg-red-500/20"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6 text-white">
      <Card className="overflow-hidden">
        <div className="border-b border-white/10 bg-gradient-to-r from-emerald-500/10 via-white/5 to-transparent p-6">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-4xl">
              <div className="flex flex-wrap items-center gap-3">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Vendor Hub
                  </button>
                )}

                <button
                  type="button"
                  onClick={() =>
                    fetchVendor({ silent: true })
                  }
                  disabled={refreshing}
                  className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-gray-300 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 ${
                      refreshing ? "animate-spin" : ""
                    }`}
                  />

                  {refreshing
                    ? "Refreshing..."
                    : "Refresh"}
                </button>
              </div>

              <div className="mt-5 text-xs uppercase tracking-[0.25em] text-emerald-300">
                Vendor Executive Intelligence
              </div>

              <h2 className="mt-2 text-3xl font-bold">
                {vendor.vendor_name ||
                  `Vendor #${vendorId}`}
              </h2>

              <p className="mt-3 text-sm leading-6 text-gray-400">
                {intelligence.message ||
                  "Executive supplier profile combining procurement, invoice, pricing and delivery signals."}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {vendor.preferred_supplier && (
                  <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300">
                    Preferred Supplier
                  </span>
                )}

                {vendor.strategic_supplier && (
                  <span className="rounded-full border border-blue-400/30 bg-blue-500/10 px-3 py-1 text-xs font-semibold text-blue-300">
                    Strategic Supplier
                  </span>
                )}

                {vendor.contract_end && (
                  <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-gray-300">
                    Contract Ends:{" "}
                    {new Date(
                      vendor.contract_end
                    ).toLocaleDateString("en-GB")}
                  </span>
                )}

                {vendor.vendor_id && (
                  <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-gray-400">
                    Vendor ID: {vendor.vendor_id}
                  </span>
                )}
              </div>
            </div>

            <div className="min-w-[180px] rounded-2xl border border-white/10 bg-black/20 p-5 text-right">
              <div
                className={`text-5xl font-black ${scoreClass(
                  scores.overall
                )}`}
              >
                {scores.overall ?? 0}
              </div>

              <div className="mt-1 text-xs uppercase tracking-wide text-gray-500">
                Overall Score
              </div>

              <span
                className={`mt-4 inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${riskClass(
                  data?.risk_level
                )}`}
              >
                {String(
                  data?.risk_level || "unknown"
                ).toUpperCase()}{" "}
                RISK
              </span>
            </div>
          </div>
        </div>

        <div className="grid gap-4 p-6 md:grid-cols-2 xl:grid-cols-5">
          <Metric
            icon={ShieldCheck}
            label="Decision"
            value={decision.label}
            tone={decision.tone}
          />

          <Metric
            icon={BadgePoundSterling}
            label="Total Spend"
            value={compactCurrency(
              summary.total_po_value
            )}
            hint={`${number(
              summary.po_count
            )} purchase orders`}
          />

          <Metric
            icon={FileText}
            label="Invoices"
            value={number(summary.invoice_count)}
            hint={currency(summary.invoice_total)}
          />

          <Metric
            icon={Truck}
            label="On-Time Rate"
            value={percent(summary.on_time_rate)}
            tone={
              Number(summary.on_time_rate || 0) >= 0.85
                ? "text-emerald-300"
                : Number(summary.on_time_rate || 0) >=
                    0.7
                  ? "text-amber-300"
                  : "text-red-300"
            }
          />

          <Metric
            icon={AlertTriangle}
            label="Outstanding"
            value={compactCurrency(
              summary.outstanding_total
            )}
            tone={
              Number(
                summary.outstanding_total || 0
              ) > 0
                ? "text-amber-300"
                : "text-emerald-300"
            }
          />
        </div>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <Card className={`p-5 ${decision.border}`}>
          <div className="flex items-start gap-4">
            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
              <DecisionIcon
                className={`h-6 w-6 ${decision.tone}`}
              />
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-gray-400">
                Recommended Executive Action
              </div>

              <h3
                className={`mt-2 text-2xl font-bold ${decision.tone}`}
              >
                {decision.label}
              </h3>

              <p className="mt-3 text-sm leading-6 text-gray-300">
                {decision.message}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="text-lg font-semibold">
            Score Breakdown
          </h3>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <ScoreBar
              label="Delivery Reliability"
              value={scores.delivery}
            />

            <ScoreBar
              label="Quality / Rejection Control"
              value={scores.quality}
            />

            <ScoreBar
              label="Backorder Control"
              value={scores.backorder_control}
            />

            <ScoreBar
              label="Pricing Position"
              value={scores.pricing}
            />

            <ScoreBar
              label="Invoice / Payment Exposure"
              value={scores.invoice_payment}
            />
          </div>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <Card className="p-5 xl:col-span-2">
          <h3 className="text-lg font-semibold">
            Risk & Decision Evidence
          </h3>

          <p className="mt-1 text-xs text-gray-500">
            The operational evidence supporting the
            recommendation above.
          </p>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {decisionPoints.map((point, index) => (
              <div
                key={`${point}-${index}`}
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3"
              >
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />

                <div className="text-sm text-gray-300">
                  {point}
                </div>
              </div>
            ))}

            {decisionPoints.length === 0 && (
              <div className="rounded-xl border border-dashed border-white/10 bg-black/10 p-4 text-sm text-gray-400 md:col-span-2">
                No decision evidence is available yet.
                More purchase order, invoice and receiving
                activity will improve this output.
              </div>
            )}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="text-lg font-semibold">
            Commercial Exposure
          </h3>

          <div className="mt-4 space-y-3">
            <Metric
              label="PO Count"
              value={number(summary.po_count)}
            />

            <Metric
              label="Average Order"
              value={currency(
                summary.average_order_value
              )}
            />

            <Metric
              label="Invoice Total"
              value={currency(summary.invoice_total)}
            />

            <Metric
              label="Paid Total"
              value={currency(summary.paid_total)}
              tone="text-emerald-300"
            />
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">
              Price Opportunity Summary
            </h3>

            <p className="text-xs text-gray-500">
              Highest unit-price saving opportunities
              detected from your own purchase history.
            </p>
          </div>

          <div className="rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-300">
            {priceOpportunities.length} opportunity(s)
          </div>
        </div>

        {topSavingProducts.length === 0 ? (
          <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 p-4 text-sm text-emerald-300">
            No strong cheaper alternatives were detected
            for this supplier’s products.
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            {topSavingProducts.map(
              (product, index) => (
                <div
                  key={`${product.product_key}-${index}`}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
                >
                  <div className="text-xs text-gray-500">
                    Saving / Unit
                  </div>

                  <div className="mt-1 text-xl font-bold text-amber-300">
                    {currency(
                      product.saving_per_unit_vs_lowest
                    )}
                  </div>

                  <div className="mt-3 line-clamp-2 text-sm font-semibold">
                    {product.description ||
                      product.item_code ||
                      product.product_key}
                  </div>

                  <div className="mt-1 text-xs text-gray-500">
                    Current{" "}
                    {currency(product.latest_price)} · Low{" "}
                    {currency(
                      product.market_low_price
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">
              Product Price Intelligence
            </h3>

            <p className="text-xs text-gray-500">
              Products supplied by this vendor, compared
              against the lowest known price in purchase
              history.
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-400">
            <CalendarClock className="h-4 w-4" />

            Last refresh:{" "}
            {data?.updated_at
              ? new Date(
                  data.updated_at
                ).toLocaleString("en-GB")
              : "—"}
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="min-w-full text-sm">
            <thead className="bg-white/[0.04] text-gray-300">
              <tr>
                <th className="p-3 text-left">
                  Product
                </th>

                <th className="p-3 text-right">
                  Latest
                </th>

                <th className="p-3 text-right">
                  Market Low
                </th>

                <th className="p-3 text-right">
                  Market Avg
                </th>

                <th className="p-3 text-right">
                  Saving / Unit
                </th>

                <th className="p-3 text-right">
                  Spend
                </th>

                <th className="p-3 text-right">
                  Vendors
                </th>
              </tr>
            </thead>

            <tbody>
              {products
                .slice(0, 50)
                .map((product, index) => (
                  <tr
                    key={`${product.product_key}-${index}`}
                    className="border-t border-white/10 hover:bg-white/[0.03]"
                  >
                    <td className="p-3">
                      <div className="font-semibold">
                        {product.description ||
                          product.item_code ||
                          product.product_key}
                      </div>

                      <div className="text-xs text-gray-500">
                        {product.item_code ||
                          product.product_key}
                      </div>
                    </td>

                    <td className="p-3 text-right">
                      {currency(
                        product.latest_price
                      )}
                    </td>

                    <td className="p-3 text-right text-emerald-300">
                      {currency(
                        product.market_low_price
                      )}
                    </td>

                    <td className="p-3 text-right">
                      {currency(
                        product.market_avg_price
                      )}
                    </td>

                    <td className="p-3 text-right text-amber-300">
                      {currency(
                        product.saving_per_unit_vs_lowest
                      )}
                    </td>

                    <td className="p-3 text-right">
                      {currency(
                        product.total_spend
                      )}
                    </td>

                    <td className="p-3 text-right">
                      {product.vendor_count || "—"}
                    </td>
                  </tr>
                ))}

              {products.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="p-6 text-center text-gray-400"
                  >
                    No product intelligence is available
                    for this vendor yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}